import { createElement, Fragment, useMemo, type CSSProperties, type ReactNode } from 'react';

import { normalizeRichText } from '../richText';

type RenderedTag = 'ul' | 'ol' | 'li' | 'p' | 'br' | 'strong' | 'em' | 'u' | 'sub' | 'sup';

// The only markup that is rendered - with no attributes at all, so no link, style, event handler
// or image can come in through the data. Everything else is unwrapped to its text (div, span,
// unknown tags) or dropped with its content (script and friends).
const RENDERED: Record<string, RenderedTag> = {
    ul: 'ul',
    ol: 'ol',
    li: 'li',
    p: 'p',
    br: 'br',
    b: 'strong',
    strong: 'strong',
    i: 'em',
    em: 'em',
    u: 'u',
    sub: 'sub',
    sup: 'sup'
};

const DROPPED = new Set(['script', 'style', 'iframe', 'object', 'embed', 'template', 'head', 'title', 'noscript']);

const STYLES: Partial<Record<RenderedTag, CSSProperties>> = {
    ul: { margin: 0, paddingLeft: '1.25em' },
    ol: { margin: 0, paddingLeft: '1.25em' },
    p: { margin: '0 0 0.4em' }
};

const renderNodes = (nodes: NodeListOf<ChildNode>, keyPrefix: string): ReactNode[] =>
    Array.from(nodes).map((node, position) => {
        const key = `${keyPrefix}${position}`;
        if (node.nodeType === Node.TEXT_NODE) {
            return <Fragment key={key}>{node.textContent}</Fragment>;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return null;
        }
        const tag = (node as Element).tagName.toLowerCase();
        if (DROPPED.has(tag)) {
            return null;
        }
        const children = renderNodes(node.childNodes, `${key}.`);
        const rendered = RENDERED[tag];
        if (!rendered) {
            return <Fragment key={key}>{children}</Fragment>;
        }
        if (rendered === 'br') {
            return <br key={key} />;
        }
        return createElement(rendered, { key, style: STYLES[rendered] }, ...children);
    });

/**
 * A text value that may carry HTML - as STIBO delivers long descriptions, escaped or not. Plain
 * text stays plain text (entities decoded); markup is parsed with DOMParser, which never runs
 * scripts, and rebuilt from the allow-list above. No dangerouslySetInnerHTML anywhere.
 *
 * Renders block elements (lists), so put it in a `<div>`-based container - e.g.
 * `<Text component="div">` - not in Mantine's default `<p>`.
 */
export const RichText = ({ value }: { value: string }) => {
    const content = useMemo(() => {
        const { text, html } = normalizeRichText(value);
        if (!html || typeof DOMParser === 'undefined') {
            return text;
        }
        const document = new DOMParser().parseFromString(text, 'text/html');
        return renderNodes(document.body.childNodes, '');
    }, [value]);

    return <>{content}</>;
};
